/**
 * Hook that provides a function to toggle the publish/unpublish state of a project instance and tracks the corresponding analytics event.
 *
 * @param {string} projectId - The unique identifier of the project
 * @param {string} instanceId - The unique identifier of the project instance
 *
 * @returns {Object} An object containing the publish/unpublish/republish functions and loading state
 * @returns {Function} returns.publishUnpublish - Function to toggle publish/unpublish state
 * @returns {Function} returns.republish - Function to (re)publish regardless of current live state
 * @returns {boolean} returns.isUpdatingStatus - Loading state of the operation
 *
 * @example
 * const { publishUnpublish, isUpdatingStatus } = usePublishUnpublish("123", "456");
 * await publishUnpublish();
 *
 * @remarks
 * - If the instance is not live (unpublished), it will be published and a publish event will be tracked
 * - If the instance is live (published), it will be unpublished and an unpublish event will be tracked
 * - `republish` always publishes, regardless of live state — used for the out-of-date "Update" action
 * - Both go through the same quota handling: a 429 opens the quota modal instead of failing silently
 */

import { useCallback, useState } from "react";
import { AnalyticsEvents } from "types/analytics/";
import { useProjectInstance } from "./useProjectInstance";
import { useAnalytics } from "hooks/useAnalytics";
import { useQuota } from "hooks/useQuota";
import { AxiosError } from "axios";
import * as Sentry from "@sentry/react";

export const usePublishUnpublish = (projectId: string, instanceId: string) => {
  const { trackEvent } = useAnalytics();
  const { refetch: refetchQuota } = useQuota();
  const [showQuotaModal, setShowQuotaModal] = useState(false);
  const {
    publish,
    unpublish,
    isLoading: isUpdatingStatus,
    isMutating: isMutatingStatus,
    isLive,
  } = useProjectInstance({
    projectId,
    instanceId,
  });

  const dismissQuotaModal = useCallback(() => {
    setShowQuotaModal(false);
  }, []);

  // Shared quota/error handling for anything that calls publish or unpublish
  // directly, so a 429 always surfaces the quota modal instead of being
  // silently swallowed or shown as a generic error.
  const withQuotaHandling = useCallback(
    async (
      action: () => Promise<void>,
      analyticsEvent: AnalyticsEvents,
      actionLabel: "publish" | "unpublish"
    ): Promise<{ quotaExceeded: boolean; success: boolean }> => {
      try {
        await action();
        trackEvent(analyticsEvent, {
          project_id: projectId,
          instance_id: instanceId,
        });
        refetchQuota();
        return { quotaExceeded: false, success: true };
      } catch (error) {
        const axiosError = error as AxiosError;

        if (axiosError.status === 429) {
          setShowQuotaModal(true);
          return { quotaExceeded: true, success: false };
        } else {
          console.error(
            "An error occurred while toggling publish/unpublish:",
            error
          );
          Sentry.captureException(error, {
            tags: {
              project_id: projectId,
              instance_id: instanceId,
              action: actionLabel,
            },
          });
          return { quotaExceeded: false, success: false };
        }
      }
    },
    [projectId, instanceId, trackEvent, refetchQuota]
  );

  const publishUnpublish = useCallback(
    () =>
      isLive
        ? withQuotaHandling(
            unpublish,
            AnalyticsEvents.ProjectExperienceUnpublish,
            "unpublish"
          )
        : withQuotaHandling(
            publish,
            AnalyticsEvents.ProjectExperiencePublish,
            "publish"
          ),
    [isLive, publish, unpublish, withQuotaHandling]
  );

  // Always (re)publishes regardless of current live state — for the
  // out-of-date "Republish" action, which is only offered while already
  // live. Goes through the same quota handling as the toggle above.
  const republish = useCallback(
    () =>
      withQuotaHandling(
        publish,
        AnalyticsEvents.ProjectExperiencePublish,
        "publish"
      ),
    [publish, withQuotaHandling]
  );

  return {
    publishUnpublish,
    republish,
    isUpdatingStatus,
    isMutatingStatus,
    showQuotaModal,
    dismissQuotaModal,
  };
};
